import { useEffect, useLayoutEffect, useState } from "react";
import PropTypes from "prop-types";
// import courseInfoData from "../courseInfo.json";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  clearCourseLearningAction,
  getCourseLearningAction,
} from "@redux/modules/course/slices";
import { useWindowSize } from "@hooks";
import { APP_TITLE_SUFFIX } from "@constants";
import { checkEnrollmentAction } from "@redux/actions";

const useDownloadCourse = (courseId, lessonId) => {
  // Local state
  const [allLessons, setAllLessons] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [currentLesson, setCurrentLesson] = useState();
  const [showFullSidebar, setShowFullSidebar] = useState(false);
  // Redux
  const { getCourseLearning } = useSelector((state) => state.course);
  const { data: courseInfo, processing } = getCourseLearning;
  const { checkEnrollment, processing: checkProcessing } = useSelector(
    (state) => state.enrollment
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { width } = useWindowSize();

  const hideSidebar = () => {
    if (width < 992) setShowFullSidebar(false);
  };

  // Check enroll
  useLayoutEffect(() => {
    dispatch(checkEnrollmentAction(courseId));
  }, [courseId]);

  // Action after check enroll
  useLayoutEffect(() => {
    if (
      checkEnrollment.isEnrolled !== null &&
      courseInfo === null &&
      !checkProcessing
    ) {
      if (checkEnrollment.isEnrolled === false) {
        navigate("/access-denied", { replace: true });
      } else {
        dispatch(getCourseLearningAction(courseId));
      }
    }
  }, [checkEnrollment.isEnrolled]);

  // Update current lesson when lesson id change
  useEffect(() => {
    if (allLessons.length !== 0) {
      if (lessonId) {
        updateLesson(lessonId);
      } else {
        setCurrentLesson(allLessons[0]);
      }
    }
  }, [lessonId, allLessons]);

  // update list all lesson and title
  useEffect(() => {
    if (courseInfo?.sections.length !== 0) {
      document.title = `${courseInfo?.title || ""} | ${APP_TITLE_SUFFIX}`;
      const lessons =
        courseInfo?.sections?.reduce(
          (arr, section) => [...arr, ...section.lessons],
          []
        ) || [];
      setAllLessons(lessons);
    }
    return () => {
      document.title = APP_TITLE_SUFFIX;
    };
  }, [courseInfo]);

  // Clear course when unmount
  useEffect(() => {
    return () => {
      dispatch(clearCourseLearningAction());
    };
  }, []);

  // Update hasNext/hasPrev
  useEffect(() => {
    const lessonIndex = allLessons.findIndex(
      (lesson) => lesson.id === currentLesson.id
    );
    if (lessonIndex === -1 || !allLessons || allLessons.length === 0) {
      setHasNext(false);
      setHasPrev(false);
    } else {
      setHasNext(true);
      setHasPrev(true);
      if (lessonIndex === 0) {
        setHasPrev(false);
      }
      if (lessonIndex === allLessons.length - 1) {
        setHasNext(false);
      }
    }
  }, [currentLesson]);

  // Logic
  const updateLesson = (_lessonId) => {
    const lesson = allLessons.find((lesson) => lesson.id === _lessonId);
    if (lesson) {
      setCurrentLesson(lesson);
      hideSidebar();
    } else {
      setCurrentLesson(allLessons.length !== 0 ? allLessons[0] : null);
    }
  };

  const handleGoBack = () => {
    // navigate(`/course/${courseId}`);
    navigate(-1);
  };

  const handleNext = () => {
    if (hasNext) {
      const lessonIndex = allLessons.findIndex(
        (lesson) => lesson.id === currentLesson.id
      );
      handleChangeLesson(allLessons[lessonIndex + 1].id);
    }
  };

  const handlePrevious = () => {
    if (hasPrev) {
      const lessonIndex = allLessons.findIndex(
        (lesson) => lesson.id === currentLesson.id
      );
      handleChangeLesson(allLessons[lessonIndex - 1].id);
    }
  };

  const handleChangeLesson = (_lessonId) => {
    navigate(`/learning/${courseId}/lesson/${_lessonId}`, { replace: true });
  };

  return {
    processing,
    courseInfo,
    showFullSidebar,
    hasNext,
    hasPrev,
    currentLesson,
    setShowFullSidebar,
    handleGoBack,
    handleNext,
    handlePrevious,
    handleChangeLesson,
  };
};

/* required: display name */
useDownloadCourse.displayName = "useDownloadCourse";

/* required: props types */
useDownloadCourse.propTypes = { courseId: PropTypes.string };

/* required: default props */
useDownloadCourse.defaultProps = { courseId: "" };

export { useDownloadCourse };
